import { useCallback } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { agentService, type Agent } from '@/lib/services/agents';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

export function useAgents() {
  const { data: agents, mutate, isLoading, error } = useSWR<Agent[]>(
    '/api/agents',
    fetcher
  );

  const deleteAgent = useCallback(async (id: string) => {
    try {
      await agentService.delete(id);
      mutate(agents?.filter(a => a.id !== id), false);
      toast.success('Agent deleted successfully');
    } catch (error) {
      console.error('Failed to delete agent:', error);
      toast.error('Failed to delete agent');
      throw error;
    }
  }, [agents, mutate]);

  const toggleAgentStatus = useCallback(async (id: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await agentService.toggleStatus(id, { status: newStatus });
      await mutate(undefined, { revalidate: true });
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (error) {
      console.error('Failed to toggle agent status:', error);
      toast.error('Failed to update agent status');
      throw error;
    }
  }, [mutate]);

  return {
    agents,
    isLoading,
    error,
    deleteAgent,
    toggleAgentStatus,
  };
}
